import R from "ramda";
import { createSlice } from "@reduxjs/toolkit";
import { useDispatch } from "react-redux";

import PeopleRepository from "repositories/PeopleRepository";
import { trackEventPersonSave } from "utils/gAnalyticsUtils";
import { getScore } from "utils/dateTimeUtils";
import { createCelebration } from "utils/calendarUtils";
import { deserialize, concat, mergeById } from "utils/storeUtils";
import { jjLogger } from "utils/logUtils";
import { DATE_TYPE } from "enums";

const initialState = {
  people: [],
  celebrations: [],
  meta: null,
  peopleToUpdate: [],
};

const peopleSlice = createSlice({
  name: "people",
  initialState,
  reducers: {
    loadPeopleSuccess(state, { payload }) {
      state.people = deserialize(payload);
      state.meta = R.propOr(null, "meta", payload);
    },
    loadAdditionalPeopleSuccess(state, { payload }) {
      state.people = R.union(deserialize(payload), state.people);
      state.meta = R.propOr(null, "meta", payload);
    },
    loadCelebrationsSuccess(state, { payload }) {
      state.celebrations = payload;
      state.meta = R.propOr(null, "meta", payload);
    },
    createPersonSuccess(state, { payload }) {
      state.people = concat(state.people, [deserialize(payload)]);
    },
    updatePersonSuccess(state, { payload }) {
      const { personId, params } = payload;
      state.people = mergeById(state.people, [], personId, params.data.attributes);
    },
    resetPeople(state) {
      state.people = [];
      state.meta = null;
    },
    addPersonToUpdate(state, { payload }) {
      const { person, face } = payload;
      const personToUpdate = {
        ...person,
        newDefaultHeadId: face.id,
        defaultHead: { ...face, default: true, type: "default-heads" },
      };
      const personIndex = R.findIndex(R.propEq("id", personToUpdate.id))(state.peopleToUpdate);
      if (personIndex === -1) {
        state.peopleToUpdate.push(personToUpdate);
      } else {
        state.peopleToUpdate = R.update(personIndex, personToUpdate, state.peopleToUpdate);
      }
    },
    resetPeopleToUpdate(state) {
      state.peopleToUpdate = [];
    },
  },
});

export const { actions } = peopleSlice;
export default peopleSlice.reducer;

export const usePeopleActions = () => {
  const dispatch = useDispatch();

  const loadPeople = (params) => {
    return PeopleRepository.index(params).then(({ data }) => {
      dispatch(peopleSlice.actions.loadPeopleSuccess(data));
      return data;
    });
  };

  const loadAdditionalPeople = (params) => {
    return PeopleRepository.index(params).then(({ data }) => {
      dispatch(peopleSlice.actions.loadAdditionalPeopleSuccess(data));
      return data;
    });
  };

  const loadCelebrations = async ({ anniversaryParams, birthdayParams }) => {
    try {
      const responses = await Promise.all([
        PeopleRepository.index(anniversaryParams),
        PeopleRepository.index(birthdayParams),
      ]);
      const dataList = responses.map((response) => response.data);
      const anniversaryData = deserialize(dataList[0]);
      const birthdayData = deserialize(dataList[1]);
      let celebrations = [];

      const todayScore = getScore(new Date());
      const nextYearCelebrations = [];
      const currentYearCelebrations = [];

      anniversaryData.forEach((person) => {
        const celebration = createCelebration(person, DATE_TYPE.anniversary);
        if (todayScore > person.score) {
          nextYearCelebrations.push(celebration);
        } else {
          currentYearCelebrations.push(celebration);
        }
      });

      birthdayData.forEach((person) => {
        const celebration = createCelebration(person, DATE_TYPE.birthday);
        if (todayScore > person.score) {
          nextYearCelebrations.push(celebration);
        } else {
          currentYearCelebrations.push(celebration);
        }
      });

      currentYearCelebrations.sort(({ score: currentScore }, { score: nextScore }) => currentScore - nextScore);
      nextYearCelebrations.sort(({ score: currentScore }, { score: nextScore }) => currentScore - nextScore);

      celebrations = [...currentYearCelebrations, ...nextYearCelebrations];

      dispatch(peopleSlice.actions.loadCelebrationsSuccess(celebrations));
      return celebrations;
    } catch (error) {
      jjLogger.log(error);
      return error;
    }
  };

  const createPerson = (params) => {
    return PeopleRepository.create(params).then(({ data }) => {
      const person = deserialize(data);
      trackEventPersonSave(person.relation);
      dispatch(peopleSlice.actions.createPersonSuccess(data));
    });
  };

  const deletePerson = (personId, params) => {
    return PeopleRepository.delete(personId, params);
  };

  const updatePerson = (personId, params) => {
    return PeopleRepository.update(personId, params).then(() => {
      dispatch(peopleSlice.actions.updatePersonSuccess({ personId, params }));
    });
  };

  const resetPeople = () => {
    dispatch(peopleSlice.actions.resetPeople());
  };

  const addPersonToUpdate = (person, face) => {
    dispatch(peopleSlice.actions.addPersonToUpdate({ person, face }));
  };

  const resetPeopleToUpdate = () => {
    dispatch(peopleSlice.actions.resetPeopleToUpdate());
  };

  return {
    loadPeople,
    loadAdditionalPeople,
    loadCelebrations,
    createPerson,
    deletePerson,
    updatePerson,
    resetPeople,
    addPersonToUpdate,
    resetPeopleToUpdate,
  };
};
